import { gql } from '@apollo/client/core';
import { captureException } from '@sentry/react';
import {
  getIdToken,
  getIdTokenResult,
  onAuthStateChanged,
  onIdTokenChanged,
  signInAnonymously,
  signOut,
} from 'firebase/auth';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { useOverrideRole } from 'core/molecules/feature-flag-manager/FeatureFlagManager';
import { fbAuth } from 'support/firebase/firebase';
import { useInterval } from 'support/react/hooks';
import {
  AuthenticationUserFragment,
  RoleName,
  UserSegment,
  useCreateInstruqtUserMutation,
  useGetInstruqtUserLazyQuery,
} from 'types/graphql';

// Refresh the authentication token every 30 minutes.
export const REFRESH_TOKEN_INTERVAL = 30 * 60 * 1000;

export interface AuthContextType {
  instruqtUser: AuthenticationUserFragment;
  isAnonymous: boolean;
  isSSO: boolean;
  login: (inviteToken?: string) => Promise<void>;
  logout: (redirect?: string) => void;
}

gql`
  mutation CreateInstruqtUser($inviteToken: String) {
    login(inviteToken: $inviteToken) {
      ...AuthenticationUser
    }
  }

  query GetInstruqtUser {
    me {
      ...AuthenticationUser
    }
  }

  fragment AuthenticationUser on User {
    id
    segments
    is_global_admin

    profile {
      email
      display_name
      avatar
      slug
    }

    teams {
      role

      node {
        id
        slug
        name

        features {
          hot_start
          invites
          statistics
          arcades
          webhooks
          track_reviews
          shared_hotstart_pools
        }
      }
    }

    ...AmplitudeUser
  }
`;

export const DEFAULT_VALUE: AuthContextType = {
  instruqtUser: {
    id: '',
    is_global_admin: false,
    profile: null,
    teams: [],
    segments: [],
  },
  isAnonymous: false,
  isSSO: false,
  login: async () => {
    // no-op.
  },
  logout: async () => {
    // no-op.
  },
};

export const AuthenticationContext = createContext<AuthContextType>(DEFAULT_VALUE);

export function AuthenticationContextProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  useTokenCookie();
  useTokenRefresh();

  const instruqtUser = useUser();
  const [loginMutation] = useCreateInstruqtUserMutation();

  if (!instruqtUser) return <LoadingImage />;

  return (
    <AuthenticationContext.Provider
      value={{
        instruqtUser,
        isAnonymous: !instruqtUser.profile?.email,
        isSSO: !fbAuth.currentUser?.providerData.some(p => p.providerId === 'password'),
        login: async (inviteToken?: string) => {
          await loginMutation({ variables: { inviteToken: inviteToken ?? null } });
        },
        logout: redirect => {
          navigate({
            pathname: '/logout',
            search: redirect ? `?${new URLSearchParams({ redirect })}` : undefined,
          });
        },
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
}

export function useAuthenticationContext() {
  return useContext(AuthenticationContext);
}

function useUser(): AuthenticationUserFragment | undefined {
  const [roleOverride] = useOverrideRole();
  const [getInstruqtUser] = useGetInstruqtUserLazyQuery({ fetchPolicy: 'network-only' });
  const [createInstruqtUser] = useCreateInstruqtUserMutation();

  const [instruqtUser, setInstruqtUser] = useState<AuthenticationUserFragment>();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(fbAuth, async user => {
      if (!user) {
        return await signInAnonymously(fbAuth);
      }

      const { data } = await getInstruqtUser();

      if (data?.me) {
        setInstruqtUser(data.me);
        return;
      }

      try {
        const { data } = await createInstruqtUser();

        if (data?.login) {
          setInstruqtUser(data.login);
        }
      } catch (error) {
        captureException(new Error('Failed to create instruqt user', { cause: error }));

        // If we run into a problem when creating the user, sign-out and try again later
        await new Promise(r => setTimeout(() => r(signOut(fbAuth)), 2000));
      }
    });

    return () => {
      unsubscribe();
    };
  }, [getInstruqtUser, createInstruqtUser]);

  if (instruqtUser && roleOverride) {
    return {
      ...instruqtUser,
      is_global_admin: false,
      segments:
        roleOverride === RoleName.Member ? [UserSegment.Member] : [UserSegment.Member, UserSegment.ContentCreator],
      teams: instruqtUser.teams.map(t => ({ ...t, role: roleOverride })),
    };
  }

  return instruqtUser;
}

/**
 * Syncs the user's token in the cookie. This cookie is used in external iframe services.
 */
function useTokenCookie() {
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(fbAuth, async user => {
      const cookieDomain = document.location.hostname.split('.').splice(-2).join('.');

      if (user) {
        const token = await getIdToken(user);
        document.cookie = `token=${token};path=/;domain=${cookieDomain};Secure;SameSite=None`;
      } else {
        document.cookie = `token=;path=/;domain=${cookieDomain};expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);
}

/**
 * Periodically refreshes the users token. This keeps the token in the cookie up to date.
 */
function useTokenRefresh() {
  useInterval(async () => {
    const { currentUser } = fbAuth;

    if (!currentUser) return;

    try {
      await getIdTokenResult(currentUser, true);
    } catch (error) {
      // This operation fails if the user's credentials expired. This error can safely be ignored, the user will be
      // signed out of Firebase.
    }
  }, REFRESH_TOKEN_INTERVAL);
}
